import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarSync, Plus, Trash2, RefreshCw, Copy, Check, Link, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CalendarSync_ = () => {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [newFeed, setNewFeed] = useState({ name: "", url: "" });
  const [adding, setAdding] = useState(false);
  const [copied, setCopied] = useState(false);

  const exportUrl = `${BACKEND_URL}/api/calendar/export.ics`;

  useEffect(() => {
    fetchFeeds();
  }, []);

  const fetchFeeds = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/calendar/feeds`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFeeds(response.data);
    } catch (error) {
      console.error("Error fetching feeds:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFeed = async () => {
    if (!newFeed.name.trim() || !newFeed.url.trim()) {
      toast.error("Nafn og URL eru nauðsynleg");
      return;
    }
    setAdding(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/calendar/feeds`, newFeed, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${newFeed.name} bætt við og samstillt!`);
      setNewFeed({ name: "", url: "" });
      fetchFeeds();
    } catch (error) {
      toast.error("Villa við að bæta við feed");
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteFeed = async (id, name) => {
    if (!window.confirm(`Eyða ${name} og öllum innfluttum dagsetningum?`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/calendar/feeds/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${name} fjarlægt`);
      fetchFeeds();
    } catch (error) {
      toast.error("Villa við að eyða feed");
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/calendar/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const total = response.data.results.reduce((sum, r) => sum + r.events_imported, 0);
      toast.success(`Samstillt! ${total} atburðir fluttir inn`);
      fetchFeeds();
    } catch (error) {
      toast.error("Villa við samstillingu");
    } finally {
      setSyncing(false);
    }
  };

  const copyExportUrl = () => {
    navigator.clipboard.writeText(exportUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <div className="flex items-center gap-3 mb-8">
          <RefreshCw className="w-8 h-8 text-primary" />
          <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tight" data-testid="calendar-sync-title">
            Dagatalssamþætting
          </h1>
        </div>

        {/* Export Section */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-heading font-semibold mb-2 flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-primary" /> Útflutningur (Export)
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Notaðu þennan hlekk til að sýna bókanirnar þínar á Airbnb, Booking.com, Google Calendar o.fl.
          </p>
          <div className="flex gap-2">
            <Input
              readOnly
              value={exportUrl}
              className="px-4 py-2 rounded-lg font-mono text-sm bg-neutral-50"
              data-testid="export-url-input"
            />
            <Button onClick={copyExportUrl} variant="outline" className="shrink-0" data-testid="copy-export-url">
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Afritaðu hlekkinn og límdu í "Import calendar" / "Subscribe to calendar" á Airbnb, Booking.com eða Google Calendar.
          </p>
        </div>

        {/* Import Section */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
                <Link className="w-5 h-5 text-primary" /> Innflutningur (Import)
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Bættu við iCal hlekk frá Airbnb, Booking.com o.fl. til að koma í veg fyrir tvíbókanir
              </p>
            </div>
            {feeds.length > 0 && (
              <Button onClick={handleSyncAll} disabled={syncing} variant="outline" size="sm" data-testid="sync-all-button">
                <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Samstilli..." : "Samstilla allt"}
              </Button>
            )}
          </div>

          {/* Add new feed */}
          <div className="bg-neutral-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-end">
              <div>
                <Label className="text-sm mb-1 block">Nafn</Label>
                <Input
                  value={newFeed.name}
                  onChange={(e) => setNewFeed({ ...newFeed, name: e.target.value })}
                  placeholder="t.d. Airbnb"
                  className="px-3 py-2 rounded-lg"
                  data-testid="feed-name-input"
                />
              </div>
              <div>
                <Label className="text-sm mb-1 block">iCal URL</Label>
                <Input
                  value={newFeed.url}
                  onChange={(e) => setNewFeed({ ...newFeed, url: e.target.value })}
                  placeholder="https://www.airbnb.com/calendar/ical/..."
                  className="px-3 py-2 rounded-lg font-mono text-sm"
                  data-testid="feed-url-input"
                />
              </div>
              <Button onClick={handleAddFeed} disabled={adding} className="bg-primary hover:bg-primary-600" data-testid="add-feed-button">
                <Plus className="w-4 h-4 mr-1" /> {adding ? "Bæti við..." : "Bæta við"}
              </Button>
            </div>
          </div>

          {/* Feeds list */}
          {feeds.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Enginn dagatal tengdur enn</p>
          ) : (
            <div className="space-y-3">
              {feeds.map((feed) => (
                <div
                  key={feed.id}
                  className="flex items-center justify-between bg-white border border-neutral-200 rounded-lg px-4 py-3"
                  data-testid={`feed-${feed.id}`}
                >
                  <div>
                    <div className="font-ui font-medium text-sm">{feed.name}</div>
                    <div className="text-xs text-muted-foreground font-mono truncate max-w-[400px]">{feed.url}</div>
                    {feed.last_synced && (
                      <div className="text-xs text-green-600 mt-0.5">
                        Síðast samstillt: {new Date(feed.last_synced).toLocaleString("is-IS")}
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => handleDeleteFeed(feed.id, feed.name)}
                    data-testid={`delete-feed-${feed.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Help */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Hvernig virkar þetta?</h3>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li><strong>Útflutningur:</strong> Afritaðu export hlekkinn og settu á Airbnb/Booking.com til að sýna bókanir þínar þar.</li>
            <li><strong>Innflutningur:</strong> Sæktu iCal hlekk frá Airbnb/Booking.com og bættu við hér. Dagsetningar frá öðrum síðum verða sjálfkrafa lokaðar.</li>
            <li>Smelltu á "Samstilla allt" til að uppfæra innfluttar dagsetningar.</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CalendarSync_;
